import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import InterfaceTransitionGraphs from './InterfaceTransitionGraphs';
import { AdversarialVerificationPaper, FseoPaper } from './PaperEssays';

const SITE_DESCRIPTION = 'Projects and essays by Cheng Hua.';

const essaysData = [
  {
    id: 'fseo',
    title: 'Floating-Strike Everlasting Options',
    date: 'March 2026',
    preview: 'A perpetual derivative for options-like convexity without strike selection or expiry management, using an EWMA floating strike and everlasting-options funding.',
    component: FseoPaper,
  },
  {
    id: 'adversarial-verification',
    title: 'Adversarial Verification',
    date: 'March 2026',
    preview: 'A market for smart-contract security where attesters stake on properties, challengers submit counterexamples, and resolution is programmatic.',
    component: AdversarialVerificationPaper,
  },
  {
    id: 'stripe-for-agents',
    title: "Stripe for Agents",
    date: "January 22, 2026",
    preview: "Two patterns are becoming increasingly clear. The number of agents will vastly exceed the number of humans. And most tasks done by humans today will be done by agents. This creates a problem...",
    content: `<p>Two patterns are becoming increasingly clear. The number of agents will vastly exceed the number of humans. And most tasks done by humans today will be done by agents.</p>

<p>This creates a problem.</p>

<p>The internet's economic foundation assumes humans are the consumers. Ads, subscriptions, sponsorships, paywalls — all of it is designed to capture human attention. You visit a site, you see an ad, the site gets paid. You find something valuable, you subscribe. The whole system runs on this exchange.</p>

<p>Agents don't work this way. They don't see ads. They don't linger. They take what they need and leave. Every time an agent completes a task a human used to do, it consumes resources from the open web while bypassing the revenue streams that fund them.</p>

<p>Software engineering is where this is most visible. It's the industry with the most agent adoption, where our two premises are most obviously true.</p>

<p>In 2022, an engineer assigned a ticket would open Stack Overflow to debug, browse Tailwind CSS docs for implementation details, scan GitHub issues. The engineer consumed content directly. Stack Overflow and Tailwind monetized that attention.</p>

<p>Now that same engineer talks to Claude Code. Agents debug the issue — referencing Stack Overflow along the way. It writes the frontend using Tailwind's documentation. The engineer is more productive than ever, but they never actually visit either site. Stack Overflow and Tailwind get nothing.</p>

<p>Stack Overflow averaged around 186,000 new questions per month in 2020-2022. Today it's under 2,300.<sup><a href="#ref1">1</a></sup> Tailwind CSS usage is at an all-time high — 75 million downloads per month, used by over half of developers — yet Tailwind Labs laid off 75% of their engineering team. Revenue dropped roughly 80%. Documentation traffic fell 40% since early 2023. Their CEO blamed AI directly.<sup><a href="#ref2">2</a></sup></p>

<p>This is what happens when agents become the primary consumers but we're still running on monetization models built for humans. The resources are just as valuable — arguably more valuable than ever — but there's no mechanism for that value to flow back to the people who created them.</p>

<p>And the resource providers aren't the only ones in a bad spot. AI companies face the same problem from the other side.</p>

<p>They have two options right now, and neither scales.</p>

<p>First, scraping. Pay expensive engineers to play cat-and-mouse with anti-scraping systems. Navigate legal gray areas. Without support from the source, you get medium to low-quality data that needs intensive cleaning. Most companies do this. It works as a bootstrap, but lawsuits are already flying, and every hour an engineer spends circumventing anti-bot measures is an hour not spent building the actual product.</p>

<p>Second, licensing deals. Pay for access directly. But licensing deals often compensate providers with a fraction of what they've already lost to AI-cannibalized traffic. And there's a deeper problem: no company can establish licensing agreements with every resource provider in every domain. For an agent to be truly general, it needs access to everything. You can't license your way there.</p>

<p>These are bootstrapping solutions. They work while agents are limited in scope. But when agents eventually handle most knowledge work, they'll need to interact with resources across every domain, in real time, at massive scale. Neither scraping or licensing would scale for the world we're heading toward. Something else is needed.</p>

<p>If agents are becoming the primary consumers of internet resources, and the current monetization systems assume human consumers, then we need new infrastructure designed for agent consumers.</p>

<p>A real-time, usage-based payment layer between agents and everything they interact with. When an agent needs something external to complete a task — documentation, code, articles, data, tools, services — it queries the source, gets what it needs, and pays. Automatically, at the point of consumption.</p>

<p>This changes things for both sides.</p>

<p>For AI companies: instead of spending engineering hours on scraping infrastructure or millions on licensing deals, they pay only for what their agents actually use. The cost is a fraction of current approaches, and it scales cleanly.</p>

<p>For resource providers: instead of watching their traffic evaporate while their content gets consumed invisibly, they get paid for actual value delivered. Not based on human attention captured, but on utility provided to agents.</p>

<p>The open web stays viable. Resources that agents rely on keep getting built and maintained because there's actually a way to get paid for them.</p>

<p>But why can't each provider just open an API and charge for access? Why is an intermediary needed at all?</p>

<p>Some will. But think about what that actually requires. Each provider has to build agent-facing infrastructure — authentication, metering, billing. Each AI company has to discover and integrate with every provider separately. That's N providers times M companies. This would lead to an explosion of bilateral integrations that doesn't scale.</p>

<p>And then there's the transaction sizes. Many of these transactions would be fractions of a cent, but legacy payment rails make anything below 30 cents economically meaningless.</p>

<p>A common platform solves both problems. Providers integrate once and become accessible to every AI company. AI companies integrate once and get access to every provider. Usage gets tracked, aggregated, and settled — providers can withdraw a meaningful amount from thousands of micro-interactions instead of trying to collect fractions of a cent from each company independently.</p>

<p>That's the gap. A payment layer purpose-built for the agent economy — one that handles the microtransactions that make agent-to-resource exchange economically viable.</p>

<p>Software engineering is just the first industry feeling this. The same dynamic will play out everywhere agents take on human tasks: education, research, legal, medical, creative work. Every domain has resources that are immensely valuable to agents but were built to monetize human attention.</p>

<p>Without new infrastructure, two things happen. First, the open resources that make agents useful become unsustainable. You can't keep building and maintaining something if agents extract all the value without compensation. Second, when new resources get built specifically for agents — and they will — there's still no way for agents and those resources to transact.</p>

<p>The way agents and resources interact right now is extremely inefficient. This isn't sustainable. A real-time payment layer for the agent economy is a necessary piece of infrastructure, and necessary infrastructure eventually gets built.</p>

<div class="references">
  <h4>References</h4>
  <ol>
    <li id="ref1" data-ref="1.">
      <a href="https://data.stackexchange.com/stackoverflow/query/1925371/new-questions-by-month#resultSets" target="_blank" rel="noopener noreferrer">Stack Overflow Data Explorer — New Questions by Month</a>
    </li>
    <li id="ref2" data-ref="2.">
      <a href="https://github.com/tailwindlabs/tailwindcss.com/pull/2388" target="_blank" rel="noopener noreferrer">Tailwind Labs GitHub — Documentation Traffic Discussion</a>
    </li>
  </ol>
</div>`
  },
  {
    id: 'three-interface-problem',
    title: "The Three Interface Problem",
    date: "September 3, 2025",
    preview: "We all interact with hundreds of interfaces every day: websites, apps, posters, presentations, etc. The vast majority of all interfaces right now are designed for humans; they are tailored to provide the best experiences for humans to comprehend and interact with, as it should be, because humans are the only entities consuming those interfaces right now...",
    content: `<p>We all interact with hundreds of interfaces every day: websites, apps, posters, presentations, etc. The vast majority of all interfaces right now are designed for humans; they are tailored to provide the best experiences for humans to comprehend and interact with, as it should be, because humans are the only entities consuming those interfaces right now. However, this will soon change.</p>

<p>Take documentation for codebases or APIs as an example. They come in text, most likely in a markdown file, because ever since computers were invented and the first bit was calculated on ENIAC, all the documentation was consumed by humans, and humans only. So it made sense to have that documentation the way it is right now. But this will no longer be the case. The increasingly obvious pattern right now is that more and more code is produced by AI instead of humans. Naturally, as a result, since AI is making the API calls and figuring out how to send the auth header to the API provider, agents are now the ones consuming the documentation.</p>

<p>Agents will soon surpass humans and become the entities that consume and interact with the documentation the most. Hence the problem: the documentation is still designed to be consumed by humans instead of agents, who are the actual entities that will be interacting with it 99% of the time.</p>

<p>Today's solutions are workarounds. People have engineered ways to bridge the gap between agents and interfaces through scraping or by relying on agent multimodality, and there are companies like BrowserUse that raised $17M to help agents navigate existing interfaces.</p>

<p>However, it is unrealistic to adapt trillions of interfaces for agents. Those interfaces are not meant for agents to begin with. A historical comparison can be made with the telephone switchboard. The physical telephone switchboard was built like this:</p>

<figure class="essay-figure">
  <img src="/Jersey Telecom Switchboard.jpg" alt="Jersey Telecom Switchboard" />
  <figcaption>Physical telephone switchboard operated by humans.</figcaption>
</figure>

<p>because the way it worked was a human would see a light, ask a caller who they wanted to connect to, and physically plug a patch cable from one jack to another. The entire interface was visual, physical, and auditory—designed for a human to operate. But with the invention of electromechanical and later electronic/digital telephone exchanges, the primary consumer of the interface changed to an automated computing system. Instead of patch panels, the interface became electrical signals and eventually digital data protocols like Signaling System 7 (SS7):</p>

<figure class="essay-figure">
  <img src="/Signaling System 7.jpg" alt="Signaling System 7" />
  <figcaption>SS7 signaling protocol: machine-to-machine communication.</figcaption>
</figure>

<p>This is a language that machines use to communicate with other machines to set up, manage, and terminate phone calls. It's a stream of data packets that is incredibly efficient but completely meaningless to a human without specialized diagnostic tools.</p>

<p>The human was removed from the loop for the core task. The interface was completely transformed from a physical panel into an invisible data protocol, entirely optimized for machines. However, modern-day interfaces have not yet made that transition for agents. The way we are using workarounds for agents to navigate through interfaces today is as absurd as building a humanoid robot to physically operate a switchboard for every modern phone call.</p>

<p>The trajectory is clear: AI agents will soon handle the vast majority of tasks that are currently performed by humans. All the legacy interfaces from those tasks, designed for humans, will be replaced by interfaces designed for AI.</p>

<p>Humans will only be interacting with very few high-level interfaces in the near future. Everything will be done behind the scenes by AI through various forms of interaction.</p>

<p>This is how I envision the future to look like, with three new forms of interfaces:</p>
<ul>
<li>The Intent Layer: interfaces where humans interact with AI at a high level</li>
<li>The Agent Layer: interfaces where AI interacts with AI</li>
<li>The AI to Everything Else Layer: interfaces where AI interacts with documentation, books, databases, etc.</li>
</ul>

<div style="margin: 32px 0;">
  <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 16px; color: #1f2937;">Current State: Human-Interface Chaos</h3>
  <div id="current-graph-container"></div>
</div>

<div style="margin: 32px 0;">
  <h3 style="font-size: 18px; font-weight: 500; margin-bottom: 16px; color: #1f2937;">Future State: AI-Mediated Hierarchy</h3>
  <div id="future-graph-container"></div>
</div>`
  }
];

const papersData = [
  {
    id: 'verifiable-agent-insurance',
    title: 'Holding Agents Accountable with Verifiable Agent Insurance',
    link: 'https://arxiv.org/abs/2610.18230',
    authors: ['Cheng Hua', 'Taro Tsuchiya', 'Nicolas Christin', 'Giulia Fanti'],
    venue: 'arXiv preprint, October 2026',
  },
];

const projectsData = [
  {
    id: 'infinite-drip',
    title: 'Infinite Drip',
    description: 'a self-funding, CPU-friendly proof-of-work faucet where anyone, including agents, can earn tokens. First place at the IC3 2026 Hackathon.',
    link: 'https://arc.net/l/quote/zlguvxts',
  },
  {
    id: 'nemo',
    title: 'Nemo',
    description: 'a search engine that lets agents discover and execute tools. 30k searches in 3 weeks.',
    link: 'https://usenemo.dev/',
  },
  {
    id: 'er-smart-glasses',
    title: 'Smart glasses for ER doctors',
    description: 'proactive AI agents with real-time audio analysis, built with Mentra.',
    link: 'https://www.linkedin.com/feed/update/urn:li:activity:7259648739059937280/',
  },
];

// "January 22, 2026" -> "January 2026" for the home list; essay pages show the full date.
const monthYear = (date) => date.replace(/^(\w+) \d+, (\d{4})$/, '$1 $2');

const HomePage = () => (
  <main className="page">
    <div className="column">
      <div className="name">Cheng Hua</div>

      <div className="bio">
        <p>
          I'm an undergraduate studying engineering at{' '}
          <a href="https://www.cmu.edu/" target="_blank" rel="noopener noreferrer">Carnegie Mellon University</a>,
          where I do research with{' '}
          <a href="https://gfanti.github.io/" target="_blank" rel="noopener noreferrer" className="nowrap">Prof. Giulia Fanti</a>
          {' '}at CyLab. I'm a member of{' '}
          <a href="https://ic3research.org/" target="_blank" rel="noopener noreferrer">IC3</a>.
        </p>
        <p>
          You can reach me at <a href="mailto:chua2@andrew.cmu.edu">chua2@andrew.cmu.edu</a>, or find me on{' '}
          <a href="https://github.com/ChengHua926" target="_blank" rel="noopener noreferrer">GitHub</a> and{' '}
          <a href="https://x.com/0xChengHua" target="_blank" rel="noopener noreferrer">Twitter</a>.
        </p>
      </div>

      <section className="section">
        <h2>Papers</h2>
        <div className="prose-list">
          {papersData.map((paper) => (
            <div key={paper.id}>
              <div>
                <a href={paper.link} target="_blank" rel="noopener noreferrer">{paper.title}</a>
              </div>
              <div>
                {paper.authors.map((author, i) => (
                  <React.Fragment key={author}>
                    {i > 0 && ', '}
                    {author === 'Cheng Hua' ? <span className="name">{author}</span> : author}
                  </React.Fragment>
                ))}
              </div>
              <div className="venue">{paper.venue}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <h2>Writing</h2>
        <div className="list">
          {essaysData.map((essay) => (
            <div key={essay.id} className="row">
              <Link to={`/essays/${essay.id}`}>{essay.title}</Link>
              <span className="date">{monthYear(essay.date)}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="section">
        <h2>Projects</h2>
        <div className="prose-list">
          {projectsData.map((project) => (
            <p key={project.id}>
              <a href={project.link} target="_blank" rel="noopener noreferrer">{project.title}</a>
              {' \u2014 '}
              {project.description}
            </p>
          ))}
        </div>
      </section>
    </div>

    <Analytics />
  </main>
);

const EssayContent = ({ post }) => {
  if (post.component) {
    const PaperComponent = post.component;
    return <PaperComponent />;
  }

  if (post.pdf) {
    return (
      <div className="pdf-note">
        <p>{post.preview}</p>
        <p>
          Full text:{' '}
          <a href={post.pdf} target="_blank" rel="noopener noreferrer">
            read the PDF →
          </a>
        </p>
      </div>
    );
  }

  if (post.id === 'three-interface-problem') {
    return (
      <div>
        <div dangerouslySetInnerHTML={{
          __html: post.content.split('<div style="margin: 32px 0;">')[0]
        }} />

        <div className="essay-graph-block">
          <h3>Current State: Human-Interface Chaos</h3>
          <InterfaceTransitionGraphs graphType="current" />
        </div>

        <div className="essay-graph-block">
          <h3>Future State: AI-Mediated Hierarchy</h3>
          <InterfaceTransitionGraphs graphType="future" />
        </div>
      </div>
    );
  }

  return <div dangerouslySetInnerHTML={{ __html: post.content }} />;
};

const EssayShell = ({ children }) => (
  <main className="page">
    <div className="column essay">
      <div className="home">
        <Link to="/">Cheng Hua</Link>
      </div>
      {children}
    </div>
    <Analytics />
  </main>
);

const Essay = () => {
  const { id } = useParams();
  const post = essaysData.find(p => p.id === id);

  useEffect(() => {
    if (post) {
      const description = post.preview || SITE_DESCRIPTION;
      document.title = `${post.title} \u2014 Cheng Hua`;
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', post.title);
      document.querySelector('meta[property="og:description"]')?.setAttribute('content', description);
      document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', post.title);
      document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', description);
      document.querySelector('meta[name="description"]')?.setAttribute('content', description);
      window.scrollTo(0, 0);
    }
    return () => { document.title = 'Cheng Hua'; };
  }, [post]);

  if (!post) {
    return (
      <EssayShell>
        <p className="essay-missing">Essay not found.</p>
      </EssayShell>
    );
  }

  return (
    <EssayShell>
      <article>
        <div>
          <h1>{post.title}</h1>
          <div className="essay-date">{post.date}</div>
        </div>
        <div className="body">
          <EssayContent post={post} />
        </div>
      </article>
    </EssayShell>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/essays/:id" element={<Essay />} />
      </Routes>
    </Router>
  );
};

export default App;
